<script lang="ts">
  import {
    BRACKETS,
    CLASSES,
    LEADERBOARD_WINDOWS,
    type Bracket,
    type LeaderboardRow,
    type LeaderboardWindow,
  } from '@arena/shared';
  import { click } from '../game/Sfx';
  import { app } from '../lib/net.svelte';

  let { onclose }: { onclose: () => void } = $props();
  let bracket = $state<Bracket>('3v3');
  let range = $state<LeaderboardWindow>('week');
  let rows = $state<LeaderboardRow[] | null>(null);
  let failed = $state(false);

  const LABELS: Record<LeaderboardWindow, string> = {
    week: 'This week',
    month: 'This month',
    all: 'All time',
  };

  $effect(() => {
    const query = `bracket=${bracket}&window=${range}`;
    rows = null;
    failed = false;
    fetch(`/api/leaderboard?${query}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: LeaderboardRow[]) => {
        if (query === `bracket=${bracket}&window=${range}`) rows = data;
      })
      .catch(() => (failed = true));
  });

  function onKey(event: KeyboardEvent) {
    if (event.code !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    click();
    onclose();
  }
</script>

<svelte:window onkeydowncapture={onKey} />

<section class="panel leaderboard">
  <header>
    <h2>Leaderboard</h2>
    <button class="close" onclick={onclose} aria-label="Close">×</button>
  </header>
  <nav class="tabs">
    {#each BRACKETS as b (b)}
      <button class:active={bracket === b} onclick={() => (bracket = b)}
        >{b}</button
      >
    {/each}
    <span class="gap"></span>
    {#each LEADERBOARD_WINDOWS as w (w)}
      <button class:active={range === w} onclick={() => (range = w)}
        >{LABELS[w]}</button
      >
    {/each}
  </nav>

  <div class="content">
    {#if failed}
      <p class="muted">Couldn't load the leaderboard.</p>
    {:else if !rows}
      <p class="muted">Loading…</p>
    {:else if !rows.length}
      <p class="muted">
        No rated {bracket} games {LABELS[range].toLowerCase()}.
      </p>
    {:else}
      <table>
        <thead>
          <tr><th>#</th><th>Name</th><th>Rating</th><th>Record</th></tr>
        </thead>
        <tbody>
          {#each rows as row, i (row.id)}
            <tr class:you={row.id === app.user?.id}>
              <td class="muted">{i + 1}</td>
              <td>
                {#each row.classes as id (id)}<img
                    src="/icons/{id}.webp"
                    alt={CLASSES[id].name}
                    title={CLASSES[id].name}
                  />{/each}
                <span
                  style:color={row.classes[0]
                    ? CLASSES[row.classes[0]].color
                    : undefined}>{row.name}</span
                >
              </td>
              <td class="rating">{row.rating}</td>
              <td class="muted">{row.wins}W – {row.losses}L</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </div>
</section>

<style>
  .leaderboard {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: grid;
    grid-template-rows: auto auto minmax(0, 1fr);
    width: min(560px, calc(100% - 24px));
    height: min(560px, calc(100% - 48px));
    padding: 0;
    pointer-events: auto;
    background: var(--panel-solid);
    border-color: var(--gold);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border);
  }
  header h2 {
    margin: 0;
  }
  .close {
    padding: 0 8px;
    font-size: 18px;
  }
  .tabs {
    display: flex;
    gap: 6px;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border);
  }
  .tabs button {
    opacity: 0.6;
  }
  .tabs button.active {
    opacity: 1;
    border-color: var(--gold);
  }
  .gap {
    flex: 1;
  }
  .content {
    overflow-y: auto;
    padding: 6px 18px 14px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th {
    font-size: 12px;
    font-weight: normal;
    letter-spacing: 0.08em;
    text-align: left;
    text-transform: uppercase;
    color: var(--muted);
  }
  th,
  td {
    padding: 4px 6px;
  }
  td img {
    width: 20px;
    height: 20px;
    margin-right: 6px;
    vertical-align: middle;
    border-radius: 3px;
  }
  tr.you {
    background: rgba(230, 192, 106, 0.12);
  }
  .rating {
    color: var(--gold);
    font-weight: bold;
  }
  .muted {
    color: var(--muted);
  }
</style>
